import * as React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Task #3134 — Sezione collassabile + campo con label per i modali form
 * (estratti da `CompanyFormModal`, consumati anche da `StoreModal`).
 *
 * `iconClassName` porta la coppia colore box/icona theme-aware
 * (es. `bg-blue-500/10 text-blue-600 dark:text-blue-400`): l'icona lucide
 * eredita `currentColor` dal box, quindi il consumer passa l'icona senza
 * classi colore. Default: `bg-primary/10 text-primary`.
 */
export interface FormSectionProps {
  title: string;
  icon: React.ReactNode;
  /** Coppia colore box/icona theme-aware. Default `bg-primary/10 text-primary`. */
  iconClassName?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export function FormSection({
  title,
  icon,
  iconClassName = 'bg-primary/10 text-primary',
  defaultOpen = true,
  children,
}: FormSectionProps): JSX.Element {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-muted/40 hover:bg-muted/60 text-left"
      >
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <div className={cn('p-1 rounded', iconClassName)}>{icon}</div>
        <span className="text-sm font-semibold text-foreground">{title}</span>
      </button>
      {open && <div className="p-4 space-y-3 bg-card">{children}</div>}
    </div>
  );
}

export interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}

export function FormField({ label, required, error, children }: FormFieldProps): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1.5">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
}
